'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var mplTokenMetadata = require('@metaplex-foundation/mpl-token-metadata');
var web3_js = require('@solana/web3.js');
var Authorization = require('../Authorization.cjs');
var DelegateInput = require('../DelegateInput.cjs');
var DelegateType = require('../DelegateType.cjs');
var Metadata = require('../models/Metadata.cjs');
var Operation = require('../../../types/Operation.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');

const TOKEN_AUTH_RULES_ID = new web3_js.PublicKey('auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg');

// -----------------
// Operation
// -----------------

const Key = 'ApproveNftDelegateOperation';

/**
 * Approve a new delegate authority for an NFT or SFT.
 *
 * ```ts
 * await metaplex.nfts().delegate({
 *   nftOrSft,
 *   delegate,
 *   delegateArgs
 * });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const approveNftDelegateOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const approveNftDelegateOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return approveNftDelegateBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Approve a new delegate authority for an NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .delegate({
 *     nftOrSft,
 *     delegate,
 *     delegateArgs
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const approveNftDelegateBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft,
    authority = metaplex.identity(),
    authorizationDetails
  } = params;

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const tokenProgram = metaplex.programs().getToken(programs);
  const systemProgram = metaplex.programs().getSystem(programs);

  // PDAs.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: nftOrSft.address,
    programs
  });
  const masterEdition = metaplex.nfts().pdas().masterEdition({
    mint: nftOrSft.address,
    programs
  });

  // New Delegate.
  const {
    delegateRecord,
    delegate,
    isTokenDelegate
  } = DelegateInput.parseTokenMetadataDelegateInput(metaplex, nftOrSft.address, params.delegate, programs);

  // Auth.
  let tokenMetadataAuthority;
  if ('__kind' in authority) {
    tokenMetadataAuthority = authority;
  } else if ('owner' in params.delegate) {
    tokenMetadataAuthority = {
      __kind: 'holder',
      owner: authority,
      token: metaplex.tokens().pdas().associatedTokenAccount({
        mint: nftOrSft.address,
        owner: authority.publicKey,
        programs
      })
    };
  } else {
    tokenMetadataAuthority = {
      __kind: 'metadata',
      updateAuthority: authority
    };
  }
  const auth = Authorization.parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: tokenMetadataAuthority,
    authorizationDetails,
    programs
  });
  const delegateArgsWithoutAuthData = params.delegate.data === undefined ? DelegateType.getDefaultDelegateArgs(params.delegate.type) : {
    __kind: params.delegate.type,
    ...params.delegate.data
  };
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer)

  // Update the metadata account.
  .add({
    instruction: mplTokenMetadata.createDelegateInstruction({
      delegateRecord,
      delegate,
      metadata,
      masterEdition: Metadata.isNonFungible(nftOrSft) ? masterEdition : undefined,
      tokenRecord: isTokenDelegate ? delegateRecord : undefined,
      mint: nftOrSft.address,
      token: auth.accounts.token,
      authority: auth.accounts.authority,
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: web3_js.SYSVAR_INSTRUCTIONS_PUBKEY,
      splTokenProgram: tokenProgram.address,
      authorizationRules: auth.accounts.authorizationRules,
      authorizationRulesProgram: TOKEN_AUTH_RULES_ID
    }, {
      delegateArgs: {
        ...delegateArgsWithoutAuthData,
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.instructionKey ?? 'approveNftDelegate'
  });
};

exports.approveNftDelegateBuilder = approveNftDelegateBuilder;
exports.approveNftDelegateOperation = approveNftDelegateOperation;
exports.approveNftDelegateOperationHandler = approveNftDelegateOperationHandler;
//# sourceMappingURL=approveNftDelegate.cjs.map
